import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ArrowLeft,
    ArrowRight,
    Award,
    BookOpen,
    Briefcase,
    Building2,
    Calendar,
    CheckCircle,
    ChevronRight,
    Clock,
    Code,
    Coffee,
    DollarSign,
    Eye,
    FileText,
    Globe,
    GraduationCap,
    Heart,
    HeartHandshake,
    LogOut,
    MapPin,
    Paperclip,
    Share2,
    Star,
    Target,
    TrendingUp,
    Upload,
    Users,
    X,
    Zap
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResumeParsingProgress from './ResumeParsingProgress';

const JobDetails = () => {
  const { id } = useParams();
  const { isAuthenticated, user, token, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [showApplication, setShowApplication] = useState(false);
  const [applicationData, setApplicationData] = useState({
    coverLetter: '',
    resume: null,
    answers: {}
  });
  const [uploadingResume, setUploadingResume] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);

  useEffect(() => {
    if (id) {
      fetchJobDetails();
      if (isAuthenticated) {
        checkApplicationStatus();
      }
    }
  }, [id, isAuthenticated]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${id}`);
      if (response.data.success) {
        setJob(response.data.data);
        console.log('🔥 JOB DETAILS - Complete job data received:', response.data.data);
      } else {
        throw new Error('Failed to fetch job details');
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const checkApplicationStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/applications?jobId=${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success && response.data.data.applications.length > 0) {
        setHasApplied(true);
      }
    } catch (error) {
      console.error('Error checking application status:', error);
    }
  };

  const handleApply = async () => {
    console.log('clicked apply');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Pre-fill application data with user's existing resume
    setApplicationData({
      coverLetter: '',
      resume: user?.resume || null, // Automatically use existing resume
      answers: {}
    });
    
    setShowApplication(true);
  };

  const submitApplication = async () => {
    if (!isAuthenticated || !token) {
      toast.error('Please log in to apply');
      navigate('/login');
      return;
    }

    setApplying(true);
    
    try {
      const submissionData = {
        coverLetter: applicationData.coverLetter,
        resume: applicationData.resume, // Include the resume
        answers: Object.entries(applicationData.answers).map(([questionIndex, answer]) => ({
          question: job.applicationProcess.questions[questionIndex].question,
          answer: answer,
          questionType: job.applicationProcess.questions[questionIndex].type
        }))
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/jobs/${id}/apply`,
        submissionData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setHasApplied(true);
        setShowApplication(false);
        setApplicationData({ coverLetter: '', resume: null, answers: {} });
        toast.success('Application submitted successfully!');
        fetchJobDetails();
      } else {
        throw new Error(response.data.message || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const handleSaveJob = () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save jobs');
      return;
    }
    
    setSaved(!saved);
    toast.success(saved ? 'Job removed from saved' : 'Job saved successfully');
  };

  const formatSalary = (salary) => {
    if (!salary) return 'Salary not specified';

    const formatNumber = (num) => {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
      return num.toString();
    };

    if (typeof salary === 'object') {
      if (salary.min && salary.max) {
        return `$${formatNumber(salary.min)} - $${formatNumber(salary.max)}`;
      }
      if (salary.min) {
        return `$${formatNumber(salary.min)}+`;
      }
      if (salary.max) {
        return `Up to $${formatNumber(salary.max)}`;
      }
      if (salary.negotiable) {
        return 'Negotiable';
      }
      // If it's an object but no min/max/negotiable, show currency/period if present
      if (salary.currency || salary.period) {
        return [salary.currency, salary.period].filter(Boolean).join(' ');
      }
      return 'Salary not specified';
    }

    if (typeof salary === 'string' || typeof salary === 'number') {
      return salary;
    }

    return 'Salary not specified';
  };

  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    
    if (typeof location === 'string') {
      return location;
    }
    
    if (typeof location === 'object') {
      const { city, state, country, isRemote } = location;
      
      if (isRemote) {
        return 'Remote';
      }
      
      const parts = [];
      if (city) parts.push(city);
      if (state) parts.push(state);
      if (country && country !== 'United States') parts.push(country);
      
      return parts.length > 0 ? parts.join(', ') : 'Location not specified';
    }
    
    return 'Location not specified';
  };

  const getTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      }
    } catch (error) {
      return 'Recently';
    }
  };

  // Helper to build full logo URL safely
  const getLogoUrl = (logo, placeholder = 'https://via.placeholder.com/100x100?text=Logo') => {
    if (!logo) return placeholder;

    let path = '';
    if (typeof logo === 'object' && logo.url) {
      path = logo.url;
    } else if (typeof logo === 'string') {
      path = logo;
    }

    if (!path) return placeholder;

    // If already absolute URL, return as-is
    if (/^https?:\/\//i.test(path)) return path;

    // Ensure leading slash
    if (!path.startsWith('/')) path = '/' + path;

    return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${path}`;
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Briefcase className="w-12 h-12 text-slate-400" />
          </div>
          <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">Job not found</h3>
          <p className="text-slate-600 mb-6">The job you're looking for doesn't exist or has been removed.</p>
          <Link to="/jobs" className="btn-primary">
            Browse Jobs
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="nav-luxury sticky top-0 z-50">
        <div className="container-luxury">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">JobZentro</h1>
                  <p className="text-xs text-slate-500 font-medium">An AI HRMS Platform</p>
                </div>
              </Link>
              {isAuthenticated && (
                <nav className="hidden lg:flex space-x-8 ml-8">
                  <Link to="/dashboard" className="nav-link">Dashboard</Link>
                  <Link to="/jobs" className="nav-link">Browse Jobs</Link>
                  <Link to="/applications" className="nav-link">Applications</Link>
                  <Link to="/profile" className="nav-link">Profile</Link>
                  <Link to="/about" className="nav-link">About</Link>
                  <Link to="/contact" className="nav-link">Contact</Link>
                </nav>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Link to="/profile" className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100 transition-colors">
                    <img
                      src={user?.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover shadow-soft"
                    />
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-heading font-semibold text-slate-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-slate-500">Job Seeker</p>
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-xl hover:bg-slate-100"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="hidden md:inline">Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                    Sign In
                  </Link>
                  <Link to="/register" className="btn-primary">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container-luxury py-8">
        {/* Back Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Link 
            to="/jobs" 
            className="inline-flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Jobs</span>
          </Link>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Job Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card-luxury p-8"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-6 flex-1">
                  <img
                    src={getLogoUrl(job.company?.logo)}
                    alt={job.company?.name || 'Company'}
                    className="w-20 h-20 rounded-2xl object-cover shadow-soft"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      {job.featured && (
                        <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-medium rounded-full">
                          <Star className="w-3 h-3 mr-1" />
                          Featured
                        </span>
                      )}
                      {job.urgent && (
                        <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm font-medium rounded-full">
                          <Zap className="w-3 h-3 mr-1" />
                          Urgent
                        </span>
                      )}
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        job.status === 'Active' ? 'bg-green-100 text-green-800' :
                        job.status === 'Paused' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                    
                    <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-4">
                      {job.title}
                    </h1>
                    
                    <div className="flex items-center space-x-2 mb-2">
                      <Building2 className="w-5 h-5 text-slate-400" />
                      <span className="text-lg font-heading font-semibold text-slate-700">{job.company?.name || 'Company Name'}</span>
                      {job.company?.verified && (
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-1 text-slate-600 mb-4">
                      <MapPin className="w-4 h-4" />
                      <span>{formatLocation(job.location)}</span>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm text-slate-500">
                      <div className="flex items-center space-x-1">
                        <Briefcase className="w-4 h-4" />
                        <span>{job.employmentType || 'Full-time'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4" />
                        <span>{job.workMode || 'On-site'}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-medium text-slate-900">{formatSalary(job.salary)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>Posted {getTimeAgo(job.createdAt)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{job.stats?.views || 0} views</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-slate-600">
                    <Users className="w-4 h-4" />
                    <span>
                      {job.applicationStats?.totalApplications || job.stats?.applications || 0} people have applied
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleSaveJob}
                      className={`p-3 rounded-xl transition-all duration-300 ${
                        saved 
                          ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                          : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                      }`}
                    >
                      <Heart className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
                    </button>
                    
                    <button className="p-3 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                      <Share2 className="w-5 h-5" />
                    </button>
                    
                    {hasApplied ? (
                      <div className="inline-flex items-center px-6 py-3 bg-green-100 text-green-800 rounded-xl font-heading font-semibold">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Applied
                      </div>
                    ) : (
                      <button
                        onClick={handleApply}
                        disabled={applying}
                        className="btn-primary px-8 py-3"
                      >
                        {applying ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Applying...
                          </>
                        ) : (
                          <>
                            Apply Now
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Job Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card-luxury p-8"
            >
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6">Job Description</h2>
              
              <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed">
                {job.description}
              </div>
              
              {job.shortDescription && (
                <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-200">
                  <h3 className="font-heading font-semibold text-blue-900 mb-2">Quick Summary</h3>
                  <p className="text-blue-700 text-sm">{job.shortDescription}</p>
                </div>
              )}
            </motion.div>

            {/* Job Requirements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-luxury p-8"
            >
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6">Requirements</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Experience Level */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    <span className="font-heading font-semibold text-slate-900">Experience Level</span>
                  </div>
                  <p className="font-medium text-slate-900">{job.level || 'Not specified'}</p>
                </div>

                {/* Department */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    <span className="font-heading font-semibold text-slate-900">Department</span>
                  </div>
                  <p className="font-medium text-slate-900">{job.department || 'Not specified'}</p>
                </div>

                {/* Employment Type */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-5 h-5 text-green-500" />
                    <span className="font-heading font-semibold text-slate-900">Employment Type</span>
                  </div>
                  <p className="font-medium text-slate-900">{job.employmentType || 'Full-time'}</p>
                </div>

                {/* Work Mode */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Globe className="w-5 h-5 text-purple-500" />
                    <span className="font-heading font-semibold text-slate-900">Work Mode</span>
                  </div>
                  <p className="font-medium text-slate-900">{job.workMode || 'On-site'}</p>
                </div>

                {/* Salary Negotiable */}
                {job.salary?.negotiable !== undefined && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-5 h-5 text-emerald-500" />
                      <span className="font-heading font-semibold text-slate-900">Salary Negotiable</span>
                    </div>
                    <p className="font-medium text-slate-900">{job.salary.negotiable ? 'Yes' : 'No'}</p>
                  </div>
                )}

                {/* Remote Work */}
                {job.location?.isRemote && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Globe className="w-5 h-5 text-cyan-500" />
                      <span className="font-heading font-semibold text-slate-900">Remote Work</span>
                    </div>
                    <p className="font-medium text-slate-900">Available</p>
                  </div>
                )}
              </div>

              {/* Experience Requirements */}
              {job.requirements?.experience && (
                <div className="mt-8">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                    <Target className="w-5 h-5 text-indigo-500 mr-2" />
                    Experience Requirements
                  </h3>
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-2xl font-bold text-slate-900">
                        {job.requirements?.experience?.min || 0} years
                      </span>
                      {job.requirements?.experience?.max && (
                        <>
                          <span className="text-slate-500">to</span>
                          <span className="text-2xl font-bold text-slate-900">
                            {job.requirements.experience.max} years
                          </span>
                        </>
                      )}
                    </div>
                    {job.requirements?.experience?.preferred && (
                      <p className="text-slate-600 mt-2">{job.requirements.experience.preferred}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Education Requirements */}
              {job.requirements?.education && (job.requirements.education.degree || job.requirements.education.fieldOfStudy) && (
                <div className="mt-8">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                    <GraduationCap className="w-5 h-5 text-green-500 mr-2" />
                    Education Requirements
                  </h3>
                  <div className="bg-slate-50 rounded-2xl p-6 space-y-3">
                    {job.requirements.education.degree && (
                      <div className="flex items-center space-x-2">
                        <BookOpen className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900 ml-2">{job.requirements.education.degree}</span>
                      </div>
                    )}
                    {job.requirements.education.fieldOfStudy && (
                      <div className="flex items-center space-x-2">
                        <Code className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900 ml-2">{job.requirements.education.fieldOfStudy}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-600">Required:</span>
                      <span className="font-medium text-slate-900">
                        {job.requirements.education.required ? 'Yes' : 'Preferred'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Skills */}
              {job.requirements?.skills && job.requirements.skills.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                    <Zap className="w-5 h-5 text-yellow-500 mr-2" />
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {job.requirements.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 rounded-xl font-medium border border-blue-200"
                      >
                        {typeof skill === 'object' && skill.name ? skill.name : skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {job.requirements?.languages && job.requirements.languages.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                    <Globe className="w-5 h-5 text-emerald-500 mr-2" />
                    Language Requirements
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {job.requirements.languages.map((lang, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 rounded-xl font-medium border border-emerald-200"
                      >
                        {typeof lang === 'object' && lang.name ? lang.name : lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {job.requirements?.certifications && job.requirements.certifications.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                    <Award className="w-5 h-5 text-purple-500 mr-2" />
                    Preferred Certifications
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {job.requirements.certifications.map((cert, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gradient-to-r from-purple-100 to-violet-100 text-purple-800 rounded-xl font-medium border border-purple-200"
                      >
                        {typeof cert === 'object' && cert.name ? cert.name : cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Requirements */}
              {job.requirements?.other && job.requirements.other.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 text-slate-500 mr-2" />
                    Additional Requirements
                  </h3>
                  <ul className="space-y-2">
                    {job.requirements.other.map((requirement, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <ChevronRight className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>

            {/* Benefits */}
            {job.benefits && Object.keys(job.benefits).some(category => job.benefits[category].length > 0) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="card-luxury p-8"
              >
                <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6 flex items-center">
                  <HeartHandshake className="w-6 h-6 text-rose-500 mr-3" />
                  Benefits & Perks
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {Object.entries(job.benefits).map(([category, benefits]) => {
                    if (benefits.length === 0) return null;
                    
                    const categoryIcons = {
                      health: Coffee,
                      financial: DollarSign,
                      timeOff: Calendar,
                      professional: BookOpen,
                      lifestyle: Heart,
                      technology: Code
                    };
                    
                    const IconComponent = categoryIcons[category] || CheckCircle;
                    
                    return (
                      <div key={category} className="space-y-3">
                        <h3 className="font-heading font-semibold text-slate-900 capitalize flex items-center">
                          <IconComponent className="w-5 h-5 text-indigo-500 mr-2" />
                          {category.replace(/([A-Z])/g, ' $1').trim()}
                        </h3>
                        <ul className="space-y-2">
                          {benefits.map((benefit, index) => (
                            <li key={index} className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-700">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card-luxury p-6"
            >
              <h3 className="text-xl font-heading font-bold text-slate-900 mb-4">About {job.company?.name || 'Company'}</h3>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-600">Industry</span>
                  </div>
                  <p className="font-medium text-slate-900">{job.company?.industry || 'Not specified'}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-sm font-medium text-slate-600">Company Size</span>
                  </div>
                  <p className="font-medium text-slate-900">{job.company?.size || 'Not specified'}</p>
                </div>

                {job.company?.description && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-slate-600">About</span>
                    <p className="text-slate-700 text-sm leading-relaxed">{job.company.description}</p>
                  </div>
                )}

                {job.company?.website && (
                  <a
                    href={job.company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline text-sm w-full justify-center"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Visit Website
                  </a>
                )}
              </div>
            </motion.div>

            {/* Application Tips */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-luxury p-6"
            >
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                <Target className="w-5 h-5 text-amber-500 mr-2" />
                Application Tips
              </h3>
              
              <ul className="space-y-3 text-sm">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Tailor your resume to match the job requirements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Write a compelling cover letter</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Research the company culture and values</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Highlight relevant skills and experiences</span>
                </li>
              </ul>
            </motion.div>

            {/* Similar Jobs */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="card-luxury p-6"
            >
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 text-blue-500 mr-2" />
                Similar Jobs
              </h3>
              
              <div className="space-y-4">
                {[1, 2, 3].map((index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors cursor-pointer">
                    <h4 className="font-heading font-semibold text-slate-900 text-sm mb-1">
                      Senior React Developer
                    </h4>
                    <p className="text-xs text-slate-600 mb-2">TechCorp Inc.</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">$80K - $120K</span>
                      <span className="text-xs text-slate-500">2d ago</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <Link to="/jobs" className="btn-outline text-sm w-full justify-center mt-4">
                View More Jobs
                <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      <AnimatePresence>
        {showApplication && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowApplication(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="card-luxury p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">
                    Apply for {job.title}
                  </h2>
                  <p className="text-slate-600">at {job.company?.name}</p>
                </div>
                <button
                  onClick={() => setShowApplication(false)}
                  className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); submitApplication(); }} className="space-y-6">
                {/* Cover Letter */}
                <div className="form-group">
                  <label className="form-label">Cover Letter *</label>
                  <textarea
                    value={applicationData.coverLetter}
                    onChange={(e) => setApplicationData(prev => ({ ...prev, coverLetter: e.target.value }))}
                    placeholder="Tell us why you're perfect for this role..."
                    rows={6}
                    className="input-luxury resize-none"
                    required
                  />
                </div>

                {/* Resume Upload */}
                <div className="form-group">
                  <label className="form-label">Resume</label>
                  
                  {/* Show existing resume if available */}
                  {user?.resume && (user.resume.originalName || user.resume.filename) && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-2xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-heading font-semibold text-green-900">
                            {user.resume.originalName || user.resume.filename || 'Your Resume.pdf'}
                          </h4>
                          <p className="text-sm text-green-700">Your uploaded resume</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => setApplicationData(prev => ({ ...prev, resume: user.resume }))}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                              applicationData.resume === user.resume
                                ? 'bg-green-600 text-white'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {applicationData.resume === user.resume ? 'Selected' : 'Use This'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Upload new resume option */}
                  <div className="border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center hover:border-blue-400 transition-colors">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-600 mb-2">
                      {(user?.resume && (user.resume.originalName || user.resume.filename)) ? 'Upload a different resume' : 'Upload your resume'}
                    </p>
                    {!(user?.resume && (user.resume.originalName || user.resume.filename)) && (
                      <p className="text-xs text-slate-500 mb-3">
                        💡 Tip: You can also upload your resume in your{' '}
                        <Link to="/profile" className="text-blue-600 hover:text-blue-800 font-medium">
                          profile settings
                        </Link>{' '}
                        for future applications
                      </p>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setApplicationData(prev => ({ ...prev, resume: file }));
                        }
                      }}
                      className="hidden"
                      id="resume-upload"
                    />
                    <label htmlFor="resume-upload" className="btn-outline text-sm cursor-pointer">
                      <Paperclip className="w-3 h-3 mr-1" />
                      Choose File
                    </label>
                    {applicationData.resume && applicationData.resume !== user?.resume && (
                      <p className="text-sm text-green-600 mt-2">{applicationData.resume.name}</p>
                    )}
                  </div>
                  
                  {/* Resume selection status */}
                  {applicationData.resume && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          Resume selected: {applicationData.resume.originalName || applicationData.resume.name || 'Resume'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Application Questions */}
                {job.applicationProcess?.questions && job.applicationProcess.questions.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-heading font-semibold text-slate-900">Additional Questions</h3>
                    {job.applicationProcess.questions.map((question, index) => (
                      <div key={index} className="form-group">
                        <label className="form-label">{question.question}</label>
                        {question.type === 'text' ? (
                          <input
                            type="text"
                            value={applicationData.answers[index] || ''}
                            onChange={(e) => setApplicationData(prev => ({
                              ...prev,
                              answers: { ...prev.answers, [index]: e.target.value }
                            }))}
                            className="input-luxury"
                            required={question.required}
                          />
                        ) : (
                          <textarea
                            value={applicationData.answers[index] || ''}
                            onChange={(e) => setApplicationData(prev => ({
                              ...prev,
                              answers: { ...prev.answers, [index]: e.target.value }
                            }))}
                            rows={3}
                            className="input-luxury resize-none"
                            required={question.required}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setShowApplication(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={applying || !applicationData.coverLetter}
                    className="btn-primary"
                  >
                    {applying ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JobDetails;